import React, { useState, useCallback, useEffect, createContext, useContext } from 'react';

// ── Types ──
export type ToastType = 'loading' | 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  duration?: number; // ms, 0 = manual dismiss only
}

interface ToastContextValue {
  show: (id: string, type: ToastType, message: string, description?: string, duration?: number) => void;
  dismiss: (id: string) => void;
  update: (id: string, updates: Partial<Omit<ToastItem, 'id'>>) => void;
  dismissAll: () => void;
}

// ── Keyframes ──
const toastKeyframes = `
@keyframes rcToastSlideIn { from { transform: translateX(-110%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
@keyframes rcToastSlideOut { from { transform: translateX(0); opacity: 1; } to { transform: translateX(-110%); opacity: 0; } }
@keyframes rcToastSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes rcToastCheckPop { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
@keyframes rcToastProgressShrink { from { width: 100%; } to { width: 0%; } }
@keyframes rcToastShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
`;

// ── Color & icon map ──
const TOAST_CONFIG: Record<ToastType, { bg: string; border: string; iconBg: string; progressColor: string }> = {
  loading: { bg: '#ffffff', border: '#e0e7ff', iconBg: 'linear-gradient(135deg, #001d5e, #002677)', progressColor: '#002677' },
  success: { bg: '#ffffff', border: '#d1fae5', iconBg: 'linear-gradient(135deg, #059669, #10b981)', progressColor: '#10b981' },
  error:   { bg: '#ffffff', border: '#fecaca', iconBg: 'linear-gradient(135deg, #dc2626, #ef4444)', progressColor: '#ef4444' },
  warning: { bg: '#ffffff', border: '#fde68a', iconBg: 'linear-gradient(135deg, #d97706, #f59e0b)', progressColor: '#f59e0b' },
  info:    { bg: '#ffffff', border: '#bfdbfe', iconBg: 'linear-gradient(135deg, #2563eb, #3b82f6)', progressColor: '#3b82f6' },
};

const ToastIcon: React.FC<{ type: ToastType }> = ({ type }) => {
  switch (type) {
    case 'loading':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'rcToastSpin 0.8s linear infinite' }}>
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    case 'success':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'rcToastCheckPop 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
          <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'error':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'warning':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'info':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2"/>
          <path d="M12 16v-4M12 8h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
  }
};

// ── Single Toast ──
const ToastCard: React.FC<{ toast: ToastItem; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  const [exiting, setExiting] = useState(false);
  const config = TOAST_CONFIG[toast.type];

  const handleDismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 260);
  }, [toast.id, onDismiss]);

  // Auto-dismiss on duration (only for non-loading)
  useEffect(() => {
    if (toast.type === 'loading' || !toast.duration) return;
    const timer = setTimeout(handleDismiss, toast.duration);
    return () => clearTimeout(timer);
  }, [toast.type, toast.duration, handleDismiss]);

  return (
    <div style={{
      minWidth: 330, maxWidth: 420, padding: '14px 18px',
      borderRadius: 14, display: 'flex', alignItems: 'flex-start', gap: 14,
      background: config.bg,
      border: `1px solid ${config.border}`,
      boxShadow: '0 10px 40px rgba(0,0,0,0.12), 0 2px 10px rgba(0,0,0,0.06)',
      animation: exiting ? 'rcToastSlideOut 0.25s ease-in forwards' : 'rcToastSlideIn 0.35s cubic-bezier(0.16,1,0.3,1)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Icon pill */}
      <div style={{
        width: 38, height: 38, borderRadius: 11, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: config.iconBg,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}>
        <ToastIcon type={toast.type} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
        <div style={{
          fontSize: 13.5, fontWeight: 650, color: '#1a1f36', lineHeight: 1.35,
          letterSpacing: '-0.01em',
        }}>
          {toast.message}
        </div>
        {toast.description && (
          <div style={{
            fontSize: 12, color: '#697386', marginTop: 4, lineHeight: 1.45,
          }}>
            {toast.description}
          </div>
        )}
        {/* Shimmer bar for loading type */}
        {toast.type === 'loading' && (
          <div style={{
            marginTop: 10, height: 3, borderRadius: 2,
            background: `linear-gradient(90deg, transparent, ${config.progressColor}44, ${config.progressColor}, ${config.progressColor}44, transparent)`,
            backgroundSize: '200% 100%',
            animation: 'rcToastShimmer 1.8s ease-in-out infinite',
          }} />
        )}
      </div>

      {/* Dismiss button (not for loading) */}
      {toast.type !== 'loading' && (
        <button onClick={handleDismiss} style={{
          width: 26, height: 26, borderRadius: 7, border: 'none',
          background: '#f3f4f6', color: '#8792a2', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, flexShrink: 0, marginTop: 1,
          transition: 'background 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.background = '#e5e7eb'}
          onMouseLeave={e => e.currentTarget.style.background = '#f3f4f6'}
        >✕</button>
      )}

      {/* Bottom progress bar for timed toasts */}
      {toast.type !== 'loading' && toast.duration && toast.duration > 0 && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, height: 3,
          background: config.progressColor,
          borderRadius: '0 0 14px 14px',
          animation: `rcToastProgressShrink ${toast.duration}ms linear forwards`,
          opacity: 0.6,
        }} />
      )}
    </div>
  );
};

// ── Context ──
const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
};

// ── Provider ──
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const show = useCallback((id: string, type: ToastType, message: string, description?: string, duration?: number) => {
    setToasts(prev => {
      const filtered = prev.filter(t => t.id !== id);
      return [...filtered, { id, type, message, description, duration }];
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const update = useCallback((id: string, updates: Partial<Omit<ToastItem, 'id'>>) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = { show, dismiss, update, dismissAll };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 && (
        <>
          <style>{toastKeyframes}</style>
          <div style={{
            position: 'fixed', top: 20, left: 20, zIndex: 9999999,
            display: 'flex', flexDirection: 'column', gap: 10,
            pointerEvents: 'none',
          }}>
            {toasts.map(toast => (
              <div key={toast.id} style={{ pointerEvents: 'auto' }}>
                <ToastCard toast={toast} onDismiss={dismiss} />
              </div>
            ))}
          </div>
        </>
      )}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
